'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Kimin için" — üç ekip profili.
 *
 * Anlatının hemen ALTINDA. Sıra okuyucunun soru sırası: "neden kurdunuz"
 * (story) → "peki bu benim için mi" (burası) → "neye göre çalışıyor"
 * (principles). Araya bir bölüm girerse okuyucu kendini sayfada bulmadan
 * ilkeleri okumaya başlar.
 *
 * Metin uydurma değil: üç profil de ürünün gerçekten karşıladığı durumları
 * anlatıyor (çalışma alanı ayrımı ajans için, onay modu onay zinciri için).
 * Sayfanın yer tutucu kısımları künye ve anlatı; bu blok değil.
 *
 * ── Neden "herkes için" demiyoruz ─────────────────────────────────────────
 *
 * Bir hakkımızda sayfasının en kolay yalanı "herkes için". Üç profili adıyla
 * saymak hem daha dürüst hem daha faydalı: okuyucu kendini bulamıyorsa bunu
 * demoya girdikten sonra değil burada anlamalı.
 *
 * ── Çizgi dili ────────────────────────────────────────────────────────────
 *
 * Üç sütun, aralarında boşluk YOK; ayracı hücrenin kendi 1px'lik hattı
 * çiziyor. Marka şeridi ve Platformlar bölümüyle aynı gerekçe: boşluk
 * verilseydi üst hatlar üç parçaya bölünür, ızgaranın tepesindeki kesintisiz
 * çizgi kaybolurdu.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Sıra: üst hat soldan çekiliyor, dikey ayraç yukarıdan iniyor, sonra içerik
 * yerleşiyor (numara → başlık → metin). Yani kutu çiziliyor, sonra doluyor —
 * dört madde bölümüyle aynı dil.
 *
 * Hücreler arası gecikme `stagger.base`: üç profil sırayla geliyor ama
 * 3 × 0.08 = 0.24 sn, kimse beklemiyor (DESIGN.md §5).
 *
 * Blur yalnızca üç metinde. Döngü ve parallax yok, elle kapatılacak hareket
 * kalmıyor.
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 */

/** Sıra ölçek sırası: tek marka → ajans → kurumsal ekip. */
const PROFILES = ['brand', 'agency', 'inhouse'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: stagger.tight } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: springFade(spring.default) },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: springFade(spring.default) },
};

const labelV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

const titleV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const textV: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

export function AboutAudience() {
  const t = useTranslations('about.audience');
  const uid = useId();

  return (
    <section className="who" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="who-frame" variants={sectionV} initial={false} animate="show">
            {/* Başlık bloğu yatay dolgu taşıyor, ızgara TAŞIMIYOR: hücrelerin
                üst hatları çerçevenin dikey hatlarına kadar uzansın diye. */}
            <motion.div className="who-head" variants={headV}>
              <p className="who-eyebrow">{t('eyebrow')}</p>
              <h2 className="who-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="who-lead">{t('lead')}</p>
            </motion.div>

            <motion.ul className="who-grid" variants={gridV}>
              {PROFILES.map((key) => (
                <motion.li className="who-cell" key={key} variants={cellV}>
                  <motion.span className="who-rule" aria-hidden="true" variants={ruleV} />
                  {/* Ayraç her hücrede basılıp CSS ile seçiliyor: yığılmış
                      yerleşimde gizli, üç sütunda yalnızca 2. ve 3. hücrede
                      görünüyor — 1. hücrenin solunda çerçevenin hattı var. */}
                  <motion.span className="who-edge" aria-hidden="true" variants={edgeV} />

                  {/* Numara dekoratif: sıra zaten listenin kendisinde. */}
                  <motion.span className="who-label" aria-hidden="true" variants={labelV}>
                    {t(`items.${key}.label`)}
                  </motion.span>

                  <motion.h3 className="who-name" variants={titleV}>
                    {t(`items.${key}.title`)}
                  </motion.h3>

                  <motion.p className="who-text" variants={textV}>
                    {t(`items.${key}.text`)}
                  </motion.p>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
